import { AuthService } from '@myosh/myosh-login';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isObject, isString } from 'lodash';
import toast from 'react-hot-toast';
import i18next from '../../i18n';
import { logout } from '../../utils/auth-util';
import { USER_TIME_ZONE } from '../../utils/date-util';
import { log } from '../../utils/log-util';

const authService = new AuthService();

export const providesList = <R extends { id: string | number }[], T extends string>(
  resultsWithIds: R | undefined,
  tagType: T
) => {
  return resultsWithIds
    ? [{ type: tagType, id: 'LIST' }, ...resultsWithIds.map(({ id }) => ({ type: tagType, id }))]
    : [{ type: tagType, id: 'LIST' }];
};

const notifyUserAndLogout = (id: string, message: string) => {
  toast.error(message, { id, duration: 3000 }); // id prevents duplicate toasts

  setTimeout(async () => {
    try {
      const userState = JSON.parse(localStorage.getItem('UserState') || '{}');
      const schema = String(userState?.user?.currentSchema);
      await logout(schema);
    } catch (_error) {
      authService.logout();
    }
  }, 3200);
};

const urlEncodeParams = (url: string) => {
  const delimiter = url.indexOf('?');
  if (delimiter !== -1) {
    const baseUrl = url.substring(0, delimiter);
    const queryString = encodeURI(url.substring(delimiter + 1) ?? '');

    return `${baseUrl}?${queryString}`;
  }

  return url;
};

const baseQuery = (apiUrl: string, apiKey: string) =>
  fetchBaseQuery({
    baseUrl: apiUrl,
    prepareHeaders: (headers) => {
      if (localStorage.getItem('UserState')) {
        const userState = JSON.parse(localStorage.getItem('UserState') || '{}');
        const schema = userState?.user?.currentSchema;
        const idToken = userState?.tokens?.id_token;
        const superUser = userState?.user?.superuser === 'true';
        const controlledUsername = userState?.user?.controlledUsername;

        if (idToken) {
          headers.set('authorization', `Bearer ${idToken}`);
        }
        if (schema) {
          headers.set('Myosh-Schema', schema);
        }
        if (superUser && controlledUsername) {
          headers.set('Controlled-Username', controlledUsername);
        }
      }
      headers.set('x-api-key', apiKey);
      headers.set('Myosh-TimeZone', USER_TIME_ZONE);

      return headers;
    },
  });

export const baseQueryWithReAuthAndUrlEncoding: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const clusterConfig = JSON.parse(localStorage.getItem('ClusterConfig') || '{}');
  if (clusterConfig.apiBaseUrl && clusterConfig.apiKey) {
    const apiUrl = `${clusterConfig.apiBaseUrl}v4`;
    const apiKey = clusterConfig.apiKey;

    let encodedUrl;
    if (isObject(args) && args.url) {
      encodedUrl = { ...args, url: urlEncodeParams(args.url) };
    } else if (isString(args)) {
      encodedUrl = urlEncodeParams(args);
    } else {
      encodedUrl = args;
    }

    let result = await baseQuery(apiUrl, apiKey)(encodedUrl, api, extraOptions);

    // handle re-auth with refreshToken
    if (result.error) {
      log(result.error); // log error to the API

      if (result.error.status === 401) {
        const refreshAuth = await authService.signinSilent();
        if (refreshAuth.refresh_token) {
          const userState = JSON.parse(localStorage.getItem('UserState') || '{}');
          userState.tokens = {
            ...userState.tokens,
            id_token: refreshAuth.id_token,
            access_token: refreshAuth.access_token,
            refresh_token: refreshAuth.refresh_token,
            expires_at: refreshAuth.expires_at,
            auth_time: Date.now(),
          };
          localStorage.setItem('UserState', JSON.stringify(userState));
          result = await baseQuery(apiUrl, apiKey)(encodedUrl, api, extraOptions);
        } else {
          notifyUserAndLogout('401', i18next.t('error-401'));
        }
      }
    }

    return result;
  } else {
    log({ message: 'The request cannot be served as the cluster configuration information is missing' });
    notifyUserAndLogout('400', i18next.t('error-loading-configuration'));

    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'Missing application configuration',
      },
    };
  }
};
